import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { getExpense, updateExpense, submitExpense, deleteExpense, getCategories, getCurrencies, getReceiptImageUrl, resetExpenseToDraft, uploadReceipt, processOCR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "../lib/utils";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Send,
  RotateCcw,
  Loader2,
  FileImage,
  FileText,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  Wallet,
  Calendar as CalendarIcon,
  Tag,
  User,
  Eye,
  ZoomIn,
  ZoomOut,
  MapPin,
  Upload,
  Camera,
  FlipHorizontal,
  Sparkles,
  X,
  Receipt
} from "lucide-react";

// Barème kilométrique 2024 (DGFIP)
const BAREME_2024 = {
  voiture: {
    "3": [0.529, 0.316, 1065, 0.370],
    "4": [0.606, 0.340, 1330, 0.407],
    "5": [0.636, 0.357, 1395, 0.427],
    "6": [0.665, 0.374, 1457, 0.447],
    "7": [0.697, 0.394, 1515, 0.470],
  },
  moto: {
    "2": [0.395, 0.099, 891,  0.248],
    "5": [0.468, 0.082, 1158, 0.275],
    "6": [0.606, 0.079, 1581, 0.343],
  },
  scooter: {
    "0": [0.272, 0.064, 624, 0.168],
  },
};

const CV_LABELS = {
  voiture: { "3": "3 CV et moins", "4": "4 CV", "5": "5 CV", "6": "6 CV", "7": "7 CV et plus" },
  moto:    { "2": "1 ou 2 CV", "5": "3 à 5 CV", "6": "Plus de 5 CV" },
  scooter: { "0": "≤ 50 cm³" },
};

function getTauxKm(typeVehicule, cvKey) {
  return BAREME_2024[typeVehicule]?.[cvKey]?.[0] || 0;
}

function calculerIndemnitesKm(km, typeVehicule, cvKey) {
  return (parseFloat(km) || 0) * getTauxKm(typeVehicule, cvKey);
}

const statusConfig = {
  draft: { label: "Brouillon", icon: Clock, color: "text-muted-foreground", bg: "bg-muted" },
  submitted: { label: "En attente", icon: Clock, color: "text-blue-600", bg: "bg-blue-100" },
  approved: { label: "Approuvé", icon: CheckCircle2, color: "text-green-600", bg: "bg-green-100" },
  rejected: { label: "Rejeté", icon: XCircle, color: "text-red-600", bg: "bg-red-100" },
  reimbursed: { label: "Remboursé", icon: Wallet, color: "text-purple-600", bg: "bg-purple-100" }
};

export default function ExpenseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, hasRole } = useAuth();
  const isManager = hasRole(["admin", "manager", "accountant"]);
  const [expense, setExpense] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(searchParams.get("edit") === "true");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [date, setDate] = useState(null);
  const [expenseMonth, setExpenseMonth] = useState("");
  const [typeVehicule, setTypeVehicule] = useState("voiture");
  const [cvFiscal, setCvFiscal] = useState("5");

  // Gestion des justificatifs en mode édition
  const [receiptsToKeep, setReceiptsToKeep] = useState([]); // IDs des justificatifs existants à conserver
  const [newReceipts, setNewReceipts] = useState([]);        // Nouveaux justificatifs uploadés
  const [newReceiptPreviews, setNewReceiptPreviews] = useState({});
  const [ocrLoading, setOcrLoading] = useState(false);

  // Caméra
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Prévisualisation
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [receiptPreviews, setReceiptPreviews] = useState({});

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (cameraOpen && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraOpen, cameraStream]);

  // Recalcul montant km si le véhicule ou les CV changent
  useEffect(() => {
    if (formData.is_mileage && formData.mileage_km) {
      const km = parseFloat(formData.mileage_km) || 0;
      const montant = calculerIndemnitesKm(km, typeVehicule, cvFiscal);
      setFormData(prev => ({ ...prev, amount: montant.toFixed(2) }));
    }
  }, [typeVehicule, cvFiscal]);

  const fetchData = async () => {
    try {
      const [expenseRes, categoriesRes, currenciesRes] = await Promise.all([
        getExpense(id),
        getCategories(),
        getCurrencies()
      ]);
      const exp = expenseRes.data;
      setExpense(exp);
      setCategories(categoriesRes.data);
      setCurrencies(currenciesRes.data);
      setDate(new Date(exp.date));
      setTypeVehicule(exp.vehicle_type || user?.vehicle_type || "voiture");
      setCvFiscal(exp.cv_fiscal || user?.cv_fiscal || "5");
      setReceiptsToKeep(exp.receipt_ids || []);
      setNewReceipts([]);
      setExpenseMonth(exp.expense_month || "");
      setFormData({
        description: exp.description,
        amount: String(exp.amount),
        currency: exp.currency,
        category_id: exp.category_id,
        vat_amount: String(exp.vat_amount || 0),
        vat_rate: String(exp.vat_rate || 20),
        is_mileage: exp.is_mileage || false,
        mileage_km: String(exp.mileage_km || ""),
        project: exp.project || "",
        notes: exp.notes || ""
      });
    } catch (error) {
      toast.error("Erreur lors du chargement");
      navigate("/expenses");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === "amount" && formData.vat_rate) {
      const amount = parseFloat(value) || 0;
      const rate = parseFloat(formData.vat_rate) || 0;
      const vat = (amount * rate) / (100 + rate);
      setFormData(prev => ({ ...prev, vat_amount: vat.toFixed(2) }));
    }

    if (field === "mileage_km" && formData.is_mileage) {
      const km = parseFloat(value) || 0;
      const montant = calculerIndemnitesKm(km, typeVehicule, cvFiscal);
      setFormData(prev => ({ ...prev, amount: montant.toFixed(2) }));
    }
  };

  // --- Caméra ---
  const openCamera = async (mode = facingMode) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      setCameraStream(stream);
      setFacingMode(mode);
      setCameraOpen(true);
    } catch (error) {
      if (error.name === "NotAllowedError") {
        toast.error("Accès à la caméra refusé.");
      } else if (error.name === "NotFoundError") {
        toast.error("Aucune caméra trouvée.");
      } else {
        toast.error("Impossible d'accéder à la caméra.");
      }
    }
  };

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
    setCameraOpen(false);
  };

  const flipCamera = async () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
    const newMode = facingMode === "environment" ? "user" : "environment";
    await openCamera(newMode);
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
    setCameraOpen(false);
    canvas.toBlob(async (blob) => {
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const file = new File([blob], `justificatif_${timestamp}.jpg`, { type: "image/jpeg" });
      try {
        const localUrl = URL.createObjectURL(file);
        const response = await uploadReceipt(file);
        const receipt = response.data;
        setNewReceiptPreviews(prev => ({ ...prev, [receipt.id]: { url: localUrl, type: file.type, name: file.name } }));
        setNewReceipts(prev => [...prev, receipt]);
        toast.success("Photo ajoutée");
      } catch {
        toast.error("Erreur lors du téléchargement de la photo");
      }
    }, "image/jpeg", 0.92);
  };

  // --- Upload fichier ---
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} est trop volumineux (max 10 MB)`);
        continue;
      }
      try {
        const localUrl = URL.createObjectURL(file);
        const response = await uploadReceipt(file);
        const receipt = response.data;
        setNewReceiptPreviews(prev => ({ ...prev, [receipt.id]: { url: localUrl, type: file.type, name: file.name } }));
        setNewReceipts(prev => [...prev, receipt]);
        toast.success(`${file.name} téléchargé`);
      } catch {
        toast.error(`Erreur lors du téléchargement de ${file.name}`);
      }
    }
    e.target.value = "";
  };

  const removeNewReceipt = (receiptId) => {
    const preview = newReceiptPreviews[receiptId];
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setNewReceiptPreviews(prev => { const n = { ...prev }; delete n[receiptId]; return n; });
    setNewReceipts(prev => prev.filter(r => r.id !== receiptId));
  };

  const removeExistingReceipt = (receiptId) => {
    setReceiptsToKeep(prev => prev.filter(id => id !== receiptId));
  };

  // --- OCR ---
  const handleOCR = async (receiptId) => {
    setOcrLoading(true);
    try {
      const response = await processOCR(receiptId);
      const data = response.data;
      if (data?.error_type === "budget_exceeded") {
        toast.error("Budget OCR épuisé.");
        return;
      }
      if (data && !data.parse_error) {
        if (data.vendor) setFormData(prev => ({ ...prev, description: data.vendor }));
        if (data.total_amount) setFormData(prev => ({ ...prev, amount: String(data.total_amount) }));
        if (data.currency) setFormData(prev => ({ ...prev, currency: data.currency }));
        if (data.vat_amount) setFormData(prev => ({ ...prev, vat_amount: String(data.vat_amount) }));
        if (data.vat_rate) setFormData(prev => ({ ...prev, vat_rate: String(data.vat_rate) }));
        if (data.date) setDate(new Date(data.date));
        toast.success("Données extraites avec succès");
      } else {
        toast.warning("Extraction partielle — vérifiez les données");
      }
    } catch {
      toast.error("Erreur lors de l'analyse OCR");
    } finally {
      setOcrLoading(false);
    }
  };

  // --- Prévisualisation ---
  const openReceiptPreview = async (receiptId, localPreview = null) => {
    if (localPreview) {
      setSelectedPreview({ id: receiptId, previewUrl: localPreview.url, previewType: localPreview.type, filename: localPreview.name });
      setZoomLevel(100);
      setPreviewOpen(true);
      return;
    }
    if (receiptPreviews[receiptId]) {
      setSelectedPreview(receiptPreviews[receiptId]);
      setZoomLevel(100);
      setPreviewOpen(true);
      return;
    }
    try {
      const response = await fetch(getReceiptImageUrl(receiptId), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const contentType = response.headers.get('content-type') || 'image/jpeg';
        const previewData = { id: receiptId, previewUrl: blobUrl, previewType: contentType, filename: `Justificatif-${receiptId.slice(0, 8)}` };
        setReceiptPreviews(prev => ({ ...prev, [receiptId]: previewData }));
        setSelectedPreview(previewData);
        setZoomLevel(100);
        setPreviewOpen(true);
      } else {
        toast.error("Impossible de charger le justificatif");
      }
    } catch {
      toast.error("Erreur lors du chargement");
    }
  };

  // --- Sauvegarde ---
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        vat_amount: parseFloat(formData.vat_amount) || 0,
        vat_rate: parseFloat(formData.vat_rate) || 20,
        mileage_km: formData.is_mileage ? (parseFloat(formData.mileage_km) || null) : null,
        mileage_rate: formData.is_mileage ? getTauxKm(typeVehicule, cvFiscal) : null,
        date: date ? format(date, "yyyy-MM-dd") : undefined,
        expense_month: expenseMonth || null,
        receipt_ids: [...receiptsToKeep, ...newReceipts.map(r => r.id)]
      };
      await updateExpense(id, payload);
      toast.success("Dépense mise à jour");
      setEditing(false);
      setNewReceipts([]);
      setNewReceiptPreviews({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setNewReceipts([]);
    setNewReceiptPreviews({});
    // Réinitialiser depuis l'état actuel de expense
    if (expense) {
      setReceiptsToKeep(expense.receipt_ids || []);
      setDate(new Date(expense.date));
    }
  };

  const handleSubmit = async () => {
    try {
      await submitExpense(id);
      toast.success("Dépense soumise pour validation");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la soumission");
    }
  };

  const handleReset = async () => {
    try {
      await resetExpenseToDraft(id);
      toast.success("Dépense remise en brouillon");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la remise en brouillon");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteExpense(id);
      toast.success("Dépense supprimée");
      navigate("/expenses");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const formatCurrency = (amount, currency = "EUR") => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount);
  };

  const canEdit = expense && (expense.status === "draft" || expense.status === "rejected") && expense.user_id === user?.id;
  const canReset = expense && expense.status !== "draft" && (expense.user_id === user?.id || isManager);

  if (loading) {
    return (
      <div className="space-y-6 max-w-4xl">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!expense) return null;

  const StatusIcon = statusConfig[expense.status]?.icon || Clock;

  // Justificatifs existants à afficher (ceux que l'on conserve + ceux retirés en mode édition)
  const visibleExistingIds = editing ? receiptsToKeep : (expense.receipt_ids || []);

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="expense-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/expenses")} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="page-title">{expense.description}</h1>
            <p className="page-description">Créé le {format(new Date(expense.created_at), "PPP", { locale: fr })}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canEdit && !editing && (
            <>
              <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-btn">
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </Button>
              <Button onClick={handleSubmit} data-testid="submit-btn">
                <Send className="h-4 w-4 mr-2" />
                Soumettre
              </Button>
            </>
          )}
          {canReset && !editing && (
            <Button variant="outline" onClick={handleReset} data-testid="reset-btn">
              <RotateCcw className="h-4 w-4 mr-2" />
              Remettre en brouillon
            </Button>
          )}
          {canEdit && (
            <Button
              variant="destructive"
              size="icon"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="delete-btn"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Banner */}
          <Card className={`${statusConfig[expense.status]?.bg} border-0`}>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <StatusIcon className={`h-6 w-6 ${statusConfig[expense.status]?.color}`} />
                <div>
                  <p className={`font-semibold ${statusConfig[expense.status]?.color}`}>
                    {statusConfig[expense.status]?.label}
                  </p>
                  {expense.status === "rejected" && expense.rejection_reason && (
                    <p className="text-sm text-red-600">Raison : {expense.rejection_reason}</p>
                  )}
                  {expense.status === "approved" && (
                    <p className="text-sm text-green-600">Approuvé par {expense.approved_by} le {format(new Date(expense.approved_at), "PPP", { locale: fr })}</p>
                  )}
                  {expense.status === "reimbursed" && (
                    <p className="text-sm text-purple-600">Remboursé le {format(new Date(expense.reimbursed_at), "PPP", { locale: fr })}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Détails</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {editing ? (
                <>
                  {/* Description */}
                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => handleInputChange("description", e.target.value)}
                      data-testid="edit-description"
                    />
                  </div>

                  {/* Catégorie + Date */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Catégorie *</Label>
                      <Select
                        value={formData.category_id}
                        onValueChange={(value) => handleInputChange("category_id", value)}
                      >
                        <SelectTrigger data-testid="edit-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground")}
                            data-testid="edit-date"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date ? format(date, "PPP", { locale: fr }) : "Sélectionner"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Toggle frais kilométrique */}
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div>
                      <Label htmlFor="mileage-toggle" className="font-medium">Frais kilométrique</Label>
                      <p className="text-sm text-muted-foreground">Barème fiscal 2024 (DGFIP)</p>
                    </div>
                    <Switch
                      id="mileage-toggle"
                      checked={formData.is_mileage}
                      onCheckedChange={(checked) => handleInputChange("is_mileage", checked)}
                      data-testid="edit-mileage-toggle"
                    />
                  </div>

                  {formData.is_mileage ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Type de véhicule</Label>
                          <Select
                            value={typeVehicule}
                            onValueChange={(v) => {
                              setTypeVehicule(v);
                              setCvFiscal(v === "voiture" ? "5" : v === "moto" ? "5" : "0");
                            }}
                          >
                            <SelectTrigger data-testid="edit-vehicle-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="voiture">Voiture</SelectItem>
                              <SelectItem value="moto">Moto (&gt; 50 cm³)</SelectItem>
                              <SelectItem value="scooter">Scooter (≤ 50 cm³)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {typeVehicule !== "scooter" && (
                          <div className="space-y-2">
                            <Label>Puissance fiscale</Label>
                            <Select value={cvFiscal} onValueChange={setCvFiscal} data-testid="edit-cv-fiscal">
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(CV_LABELS[typeVehicule]).map(([key, label]) => (
                                  <SelectItem key={key} value={key}>{label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Distance (km)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="Ex: 150"
                          value={formData.mileage_km}
                          onChange={(e) => handleInputChange("mileage_km", e.target.value)}
                          data-testid="edit-mileage-km"
                        />
                        {formData.mileage_km && (() => {
                          const km = parseFloat(formData.mileage_km) || 0;
                          const taux = getTauxKm(typeVehicule, cvFiscal);
                          return (
                            <p className="text-sm text-muted-foreground">
                              {km} km × {taux.toFixed(3).replace(".", ",")} €/km
                              {" = "}
                              <span className="font-semibold text-foreground">{(km * taux).toFixed(2)} €</span>
                            </p>
                          );
                        })()}
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Montant + devise */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Montant TTC *</Label>
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.01"
                              value={formData.amount}
                              onChange={(e) => handleInputChange("amount", e.target.value)}
                              className="pr-16"
                              data-testid="edit-amount"
                            />
                            <Select
                              value={formData.currency}
                              onValueChange={(value) => handleInputChange("currency", value)}
                            >
                              <SelectTrigger className="absolute right-0 top-0 w-20 border-0 bg-transparent">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {currencies.map((curr) => (
                                  <SelectItem key={curr.code} value={curr.code}>{curr.code}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Taux TVA (%)</Label>
                          <Select
                            value={formData.vat_rate}
                            onValueChange={(value) => {
                              const amount = parseFloat(formData.amount) || 0;
                              const rate = parseFloat(value) || 0;
                              const vat = (amount * rate) / (100 + rate);
                              setFormData(prev => ({ ...prev, vat_rate: value, vat_amount: vat.toFixed(2) }));
                            }}
                          >
                            <SelectTrigger data-testid="edit-vat-rate">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0% — Exonéré</SelectItem>
                              <SelectItem value="2.1">2,1% — Super réduit</SelectItem>
                              <SelectItem value="5.5">5,5% — Réduit</SelectItem>
                              <SelectItem value="10">10% — Intermédiaire</SelectItem>
                              <SelectItem value="20">20% — Normal</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {formData.vat_amount && (
                        <div className="space-y-2">
                          <Label>Montant TVA</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.vat_amount}
                            onChange={(e) => handleInputChange("vat_amount", e.target.value)}
                            data-testid="edit-vat-amount"
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Projet */}
                  <div className="space-y-2">
                    <Label>Projet (optionnel)</Label>
                    <Input
                      value={formData.project}
                      onChange={(e) => handleInputChange("project", e.target.value)}
                      data-testid="edit-project"
                    />
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label>Notes (optionnel)</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => handleInputChange("notes", e.target.value)}
                      rows={3}
                      data-testid="edit-notes"
                    />
                  </div>

                  {/* Mois de la note de frais */}
                  <div className="space-y-2">
                    <Label>Mois de la note de frais</Label>
                    <div className="flex gap-2">
                      <Select
                        value={expenseMonth ? expenseMonth.split("-")[1] : ""}
                        onValueChange={(m) => {
                          const y = expenseMonth ? expenseMonth.split("-")[0] : String(new Date().getFullYear());
                          setExpenseMonth(`${y}-${m}`);
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Mois" />
                        </SelectTrigger>
                        <SelectContent>
                          {["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"].map((nom, i) => (
                            <SelectItem key={i+1} value={String(i+1).padStart(2,"0")}>{nom}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={expenseMonth ? expenseMonth.split("-")[0] : ""}
                        onValueChange={(y) => {
                          const m = expenseMonth ? expenseMonth.split("-")[1] : String(new Date().getMonth()+1).padStart(2,"0");
                          setExpenseMonth(`${y}-${m}`);
                        }}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue placeholder="Année" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Boutons */}
                  <div className="flex gap-2 pt-4">
                    <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                      {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Enregistrer
                    </Button>
                    <Button variant="outline" onClick={handleCancelEdit} data-testid="cancel-edit-btn">
                      Annuler
                    </Button>
                  </div>
                </>
              ) : (
                <div className="grid gap-4">
                  <div className="flex items-center gap-3">
                    <Tag className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Catégorie</p>
                      <p className="font-medium">{expense.category_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{format(new Date(expense.date), "PPP", { locale: fr })}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Collaborateur</p>
                      <p className="font-medium">{expense.user_name}</p>
                    </div>
                  </div>
                  {expense.project && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Projet</p>
                        <p className="font-medium">{expense.project}</p>
                      </div>
                    </div>
                  )}
                  {expense.is_mileage && (
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Frais kilométrique</p>
                      <p className="font-medium">{expense.mileage_km} km × {expense.mileage_rate} €/km</p>
                    </div>
                  )}
                  {expense.notes && (
                    <div className="pt-4 border-t">
                      <p className="text-sm text-muted-foreground mb-1">Notes</p>
                      <p className="text-sm">{expense.notes}</p>
                    </div>
                  )}
                  {expense.expense_month && (
                    <div className="flex items-center gap-3">
                      <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Mois de la note de frais</p>
                        <p className="font-medium">
                          {new Date(expense.expense_month + "-01").toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Justificatifs */}
          {(visibleExistingIds.length > 0 || newReceipts.length > 0 || editing) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Justificatifs
                </CardTitle>
                {editing && (
                  <CardDescription>Ajoutez ou retirez des justificatifs</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Justificatifs existants */}
                {visibleExistingIds.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {visibleExistingIds.map((receiptId) => {
                      const preview = receiptPreviews[receiptId];
                      return (
                        <div
                          key={receiptId}
                          className="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted transition-colors group"
                        >
                          {preview ? (
                            <img src={preview.previewUrl} alt="Justificatif" className="h-12 w-12 object-cover rounded cursor-pointer" onClick={() => openReceiptPreview(receiptId)} />
                          ) : (
                            <FileImage className="h-8 w-8 text-muted-foreground" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">Justificatif</p>
                            <p className="text-xs text-muted-foreground">Cliquez pour voir</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openReceiptPreview(receiptId)}>
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            </Button>
                            {editing && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => removeExistingReceipt(receiptId)}
                                title="Retirer ce justificatif"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Nouveaux justificatifs (mode édition) */}
                {editing && newReceipts.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {newReceipts.map((receipt) => {
                      const preview = newReceiptPreviews[receipt.id];
                      const isImage = receipt.content_type?.startsWith('image');
                      const isPdf = receipt.content_type === 'application/pdf';
                      return (
                        <div key={receipt.id} className="relative group border rounded-lg overflow-hidden bg-muted">
                          <div className="aspect-[4/3] relative cursor-pointer" onClick={() => openReceiptPreview(receipt.id, preview)}>
                            {preview && isImage ? (
                              <img src={preview.url} alt={receipt.filename} className="w-full h-full object-cover" />
                            ) : isPdf ? (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-red-50">
                                <FileText className="h-12 w-12 text-red-500 mb-2" />
                                <span className="text-xs text-red-600 font-medium">PDF</span>
                              </div>
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center">
                                <FileImage className="h-12 w-12 text-muted-foreground mb-2" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Eye className="h-8 w-8 text-white" />
                            </div>
                          </div>
                          <div className="p-3">
                            <p className="text-sm font-medium truncate mb-2">{receipt.filename}</p>
                            <div className="flex items-center gap-2">
                              <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={() => handleOCR(receipt.id)} disabled={ocrLoading}>
                                {ocrLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" />OCR</>}
                              </Button>
                              <Button type="button" variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => removeNewReceipt(receipt.id)}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Zone d'upload (mode édition uniquement) */}
                {editing && (
                  <div className="space-y-3 pt-2">
                    <div
                      className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => document.getElementById('edit-receipt-upload').click()}
                    >
                      <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                      <p className="text-sm text-muted-foreground mb-1">Cliquez pour ajouter un justificatif</p>
                      <p className="text-xs text-muted-foreground">JPEG, PNG, WebP ou PDF (max 10 MB)</p>
                      <input
                        id="edit-receipt-upload"
                        type="file"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        multiple
                        className="hidden"
                        onChange={handleFileUpload}
                        data-testid="edit-receipt-upload"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 border-t" />
                      <span className="text-xs text-muted-foreground">ou</span>
                      <div className="flex-1 border-t" />
                    </div>
                    <Button type="button" variant="outline" className="w-full" onClick={() => openCamera()}>
                      <Camera className="h-4 w-4 mr-2" />
                      Prendre une photo
                    </Button>
                    <canvas ref={canvasRef} className="hidden" />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Summary Sidebar */}
        <div>
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="text-lg">Montant</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {editing ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Montant HT</span>
                    <span className="tabular-nums">
                      {formData.amount && formData.vat_amount
                        ? (parseFloat(formData.amount) - parseFloat(formData.vat_amount)).toFixed(2)
                        : "0.00"} {formData.currency}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">TVA ({formData.vat_rate}%)</span>
                    <span className="tabular-nums">{formData.vat_amount || "0.00"} {formData.currency}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-semibold">
                    <span>Total TTC</span>
                    <span className="tabular-nums text-xl">{formData.amount || "0.00"} {formData.currency}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Montant HT</span>
                    <span className="tabular-nums">
                      {formatCurrency(expense.amount - (expense.vat_amount || 0), expense.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">TVA ({expense.vat_rate}%)</span>
                    <span className="tabular-nums">
                      {formatCurrency(expense.vat_amount || 0, expense.currency)}
                    </span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-semibold">
                    <span>Total TTC</span>
                    <span className="tabular-nums text-xl">
                      {formatCurrency(expense.amount, expense.currency)}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cette dépense ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Camera Dialog */}
      <Dialog open={cameraOpen} onOpenChange={(open) => { if (!open) closeCamera(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Prendre une photo du justificatif</span>
              <Button type="button" variant="ghost" size="sm" onClick={flipCamera} title="Retourner la caméra">
                <FlipHorizontal className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={closeCamera}>Annuler</Button>
              <Button type="button" className="flex-1" onClick={capturePhoto}>
                <Camera className="h-4 w-4 mr-2" />
                Capturer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="truncate pr-4">{selectedPreview?.filename}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setZoomLevel(prev => Math.max(25, prev - 25))} disabled={zoomLevel <= 25}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm w-12 text-center">{zoomLevel}%</span>
                <Button variant="outline" size="sm" onClick={() => setZoomLevel(prev => Math.min(200, prev + 25))} disabled={zoomLevel >= 200}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-muted rounded-lg p-4 min-h-[400px]">
            {selectedPreview && (
              selectedPreview.previewType === 'application/pdf' ? (
                <iframe src={selectedPreview.previewUrl} className="w-full h-full min-h-[500px] rounded border" title={selectedPreview.filename} />
              ) : (
                <div className="flex items-center justify-center min-h-full">
                  <img
                    src={selectedPreview.previewUrl}
                    alt={selectedPreview.filename}
                    className="max-w-full h-auto transition-transform duration-200"
                    style={{ transform: `scale(${zoomLevel / 100})` }}
                  />
                </div>
              )
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
